import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { resolveEventEnd } from "../lib/events";
import type { VenueClosingTimes } from "../lib/types";

// The "On Now" strip — compact horizontal card (image left, content right)
// that surfaces above the events list when an event is currently live.
// Alive without being loud: a 3-bar equalizer replaces the static dot, and
// a "2h 15m left" counter ticks live every 60s.
//
// Two visual modes (`variant`):
//   - "app": inside the native / app-style venue home (uses --accent / --surface tokens)
//   - "web": inside the sitePages editorial layout (uses --venue-text + venue accent)

type LiveEvent = {
  id: string;
  title: string;
  artist?: string;
  date: string | Date;
  endDate?: string | Date | null;
  imageUrl?: string | null;
  externalTicketsUrl?: string | null;
};

const EQ_BARS = [
  { delay: "0s", duration: "0.9s", height: "60%" },
  { delay: "0.15s", duration: "1.1s", height: "80%" },
  { delay: "0.3s", duration: "0.85s", height: "50%" },
];

// Live-updating "1h 34m left" / "24m left". Recomputes every 60s.
function useTimeLeft(endAt: Date): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const ms = endAt.getTime() - now.getTime();
  if (ms <= 0) return "ending";
  const totalMin = Math.round(ms / 60_000);
  if (totalMin < 60) return `${totalMin}m left`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h left` : `${h}h ${m}m left`;
}

function Equalizer({ colorVar }: { colorVar: string }) {
  return (
    <>
      <span
        aria-hidden
        className="inline-flex items-end gap-[2px]"
        style={{ height: 10, width: 12 }}
      >
        {EQ_BARS.map((b, i) => (
          <span
            key={i}
            style={{
              display: "inline-block",
              width: 2,
              height: b.height,
              background: colorVar,
              borderRadius: 1,
              transformOrigin: "bottom",
              animation: `on-now-eq ${b.duration} ease-in-out ${b.delay} infinite`,
            }}
          />
        ))}
      </span>
      <style>{`
        @keyframes on-now-eq {
          0%, 100% { transform: scaleY(0.35); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </>
  );
}

export function LiveEventStrip({
  event,
  closingTimes,
  variant,
  venueLogoUrl,
  venueName,
}: {
  event: LiveEvent;
  closingTimes?: VenueClosingTimes;
  variant: "app" | "web";
  venueLogoUrl?: string | null;
  venueName?: string;
}) {
  const endAt = resolveEventEnd(event, closingTimes);
  const timeLeft = useTimeLeft(endAt);

  // Style tokens per variant. app-mode uses the CSS custom properties the
  // rest of the venue app uses; web-mode uses the editorial site tokens
  // (--venue-text + --accent) so the strip inherits the venue's palette.
  const tokens = variant === "app"
    ? {
        surface: "var(--surface)",
        surface2: "var(--surface-2)",
        border: "var(--border-default)",
        accent: "var(--accent)",
        accentFg: "var(--accent-fg)",
        fg: "var(--foreground)",
        muted: "var(--muted)",
      }
    : {
        surface: "color-mix(in srgb, var(--venue-text) 4%, transparent)",
        surface2: "color-mix(in srgb, var(--venue-text) 8%, transparent)",
        border: "color-mix(in srgb, var(--venue-text) 12%, transparent)",
        accent: "var(--accent)",
        accentFg: "var(--accent-fg, #000)",
        fg: "var(--venue-text)",
        muted: "color-mix(in srgb, var(--venue-text) 60%, transparent)",
      };

  const content = (
    <div
      className="flex items-stretch gap-3 overflow-hidden rounded-[10px] transition hover:opacity-95"
      style={{ background: tokens.surface, border: `1px solid ${tokens.border}` }}
    >
      {/* Square poster (or venue logo fallback) */}
      <div
        className="relative shrink-0 overflow-hidden"
        style={{ width: 88, height: 88, background: tokens.surface2 }}
      >
        {event.imageUrl ? (
          <img src={event.imageUrl} alt="" className="h-full w-full object-cover" />
        ) : venueLogoUrl ? (
          <img src={venueLogoUrl} alt={venueName ?? ""} className="h-full w-full object-cover opacity-70" />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center pr-3 py-2">
        {/* Alive-badge row: pulsing equalizer + ON NOW + live time-left */}
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em]">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-[3px]" style={{ background: tokens.accent, color: tokens.accentFg }}>
            <Equalizer colorVar={tokens.accentFg} />
            On Now
          </span>
          <span style={{ color: tokens.muted }}>{timeLeft}</span>
        </div>
        <p
          className="mt-1.5 truncate text-[15px] font-semibold"
          style={{ color: tokens.fg }}
          title={event.title}
        >
          {event.title}
        </p>
        {event.artist && (
          <p className="truncate text-[12px]" style={{ color: tokens.muted }} title={event.artist}>
            {event.artist}
          </p>
        )}
      </div>
    </div>
  );

  return event.externalTicketsUrl ? (
    <a
      href={event.externalTicketsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      {content}
    </a>
  ) : (
    <Link to="/events/$id" params={{ id: event.id }} className="block">
      {content}
    </Link>
  );
}
